import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Home, ShieldCheck, Newspaper, User, GraduationCap } from "lucide-react";
import { useLang } from "../i18n";
import { ASSETS } from "../data/safety";

export const Layout = ({ children }) => {
  const { t, lang, toggle } = useLang();
  const location = useLocation();

  const navItems = [
    { to: "/", label: t.nav.home, icon: Home, end: true },
    { to: "/safety", label: t.nav.safety, icon: ShieldCheck },
    { to: "/kurs", label: t.nav.course, icon: GraduationCap },
    { to: "/feed", label: t.nav.feed, icon: Newspaper },
    { to: "/about", label: t.nav.about, icon: User },
  ];

  return (
    <div className="grain min-h-screen bg-nacht text-wachs flex flex-col">
      {/* Top bar */}
      <header className="sticky top-0 z-40 border-b border-bronze/15 bg-nacht/85 backdrop-blur-xl">
        <div className="mx-auto max-w-6xl px-4 h-16 flex items-center justify-between">
          <NavLink to="/" className="flex items-center gap-2.5 group" data-testid="brand-logo-link">
            <img src={ASSETS.logo} alt="Circle of Ikarus" className="h-9 w-auto object-contain" />
            <span className="font-serif text-lg tracking-wide text-wachs group-hover:text-sonne transition-colors">
              Circle of Ikarus
            </span>
          </NavLink>

          <div className="flex items-center gap-4">
            <nav className="hidden md:flex items-center gap-1">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  data-testid={`nav-${item.to === "/" ? "home" : item.to.slice(1)}`}
                  className={({ isActive }) =>
                    `px-3 py-2 text-sm rounded-md transition-colors ${
                      isActive ? "text-bronze-soft" : "text-wachs-dim hover:text-wachs"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
            </nav>
            <button
              onClick={toggle}
              data-testid="lang-toggle"
              className="instrument-label px-2.5 py-1.5 rounded border border-bronze/30 text-bronze-soft hover:bg-bronze/10 transition-colors"
            >
              {lang === "de" ? "EN" : "DE"}
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="relative z-10 flex-1 pb-24 md:pb-0">{children}</main>

      {/* Footer */}
      <footer className="relative z-10 border-t border-bronze/15 bg-tiefsee/40 mt-10">
        <div className="mx-auto max-w-6xl px-4 py-10 grid gap-6 md:grid-cols-3">
          <div>
            <div className="flex items-center gap-2.5">
              <img src={ASSETS.logo} alt="" className="h-8 w-auto object-contain" />
              <span className="font-serif text-lg">Circle of Ikarus</span>
            </div>
            <p className="mt-1 instrument-label text-bronze-soft">Springpferde · Austria</p>
            <p className="mt-3 text-sm text-wachs-dim max-w-xs">{t.common.tagline}</p>
            <NavLink to="/staelle" data-testid="footer-stalls-link" className="mt-3 inline-block text-sm text-bronze-soft hover:text-sonne transition-colors">
              {lang === "de" ? "Für Ställe & Vereine →" : "For stables & clubs →"}
            </NavLink>
          </div>
          <div className="md:col-span-2">
            <p className="instrument-label text-bronze-soft mb-2">{t.safety.disclaimerTitle}</p>
            <p className="text-xs leading-relaxed text-wachs-dim font-mono">{t.footer.disclaimer}</p>
            <p className="mt-4 text-xs text-wachs-dim">
              © {new Date().getFullYear()} Circle of Ikarus · circleofikarus.com · {t.footer.rights}
            </p>
          </div>
        </div>
      </footer>

      {/* Bottom nav (mobile app feel) */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-50 border-t border-bronze/20 bg-nacht/95 backdrop-blur-xl">
        <div className="grid grid-cols-5">
          {navItems.map((item) => {
            const Icon = item.icon;
            const active = item.end ? location.pathname === "/" : location.pathname.startsWith(item.to);
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                data-testid={`bottomnav-${item.to === "/" ? "home" : item.to.slice(1)}`}
                className="flex flex-col items-center justify-center gap-1 py-2.5"
              >
                <Icon size={20} className={active ? "text-bronze-soft" : "text-wachs-dim"} strokeWidth={active ? 2.4 : 1.8} />
                <span className={`text-[10px] ${active ? "text-bronze-soft" : "text-wachs-dim"}`}>{item.label}</span>
              </NavLink>
            );
          })}
        </div>
      </nav>
    </div>
  );
};
